import React, { useEffect, useRef } from "react";
import { Plus, X } from "lucide-react";

import { MAX_LIST_ITEMS } from "./editorRules";
import { TEXT } from "./editorText";

const T = TEXT.form;

/**
 * A numbered list of text rows: edit a row, remove it, add another. Enter in a row moves to the next one; in the last row it
 * adds a new row at the end and moves there.
 * Props: id, label, hint, items (string[]), onChange(index, value), onAdd(), onRemove(index).
 */
export default function NumberedList({ id, label, hint, items, onChange, onAdd, onRemove }) {
  const full = items.length >= MAX_LIST_ITEMS;
  const inputs = useRef([]);
  const focusLast = useRef(false);

  useEffect(() => {
    if (!focusLast.current) return;
    focusLast.current = false;
    const last = inputs.current[items.length - 1];
    if (last) last.focus();
  }, [items.length]);

  const onKeyDown = (event, index) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    if (index < items.length - 1) {
      inputs.current[index + 1].focus();
    } else if (!full) {
      focusLast.current = true;
      onAdd();
    }
  };
  return (
    <div className="le-field" role="group" aria-labelledby={`${id}-label`} aria-describedby={`${id}-hint`}>
      <div className="le-field-head">
        <span className="le-label" id={`${id}-label`}>{label}</span>
        <span className="le-muted" id={`${id}-hint`}>{hint}</span>
      </div>
      <ol className="le-numbered">
        {items.map((item, i) => (
          <li key={i} className="le-row">
            <input className="le-input" type="text" value={item} aria-label={`${label}: ${T.item} ${i + 1}`} ref={(el) => { inputs.current[i] = el; }}
              onChange={(e) => onChange(i, e.target.value)} onKeyDown={(e) => onKeyDown(e, i)} />
            <button type="button" className="le-row-remove" aria-label={`${T.remove} ${i + 1} (${label})`} onClick={() => onRemove(i)}>
              <X size={14} aria-hidden="true" />
            </button>
          </li>
        ))}
      </ol>
      <div className="le-field-actions">
        <button type="button" className="le-btn le-btn--ghost le-btn--sm" disabled={full} aria-label={`${T.add} (${label})`} onClick={onAdd}>
          <Plus size={14} aria-hidden="true" /> {T.add}
        </button>
      </div>
    </div>
  );
}
